import React, { useState } from "react";
import { db } from "../../../firebaseConfig";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import styles from "./Timetable.module.css";

const DEPARTMENTS = [
    { label: "Computer Science", value: "CS" }
];

const PROGRAMS = [
    { label: "BS Computer Science", value: "BCS" },
    { label: "BS Software Engineering", value: "BSE" }
];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

export default function ViewTimetable() {
    const [department, setDepartment] = useState(DEPARTMENTS[0].value);
    const [program, setProgram] = useState(PROGRAMS[0].value);
    const [semester, setSemester] = useState("1");
    const [section, setSection] = useState("1");

    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [editMode, setEditMode] = useState(false);

    const handleFetch = async () => {
        setMessage("");
        setSlots([]);
        setEditMode(false);
        setLoading(true);

        try {
            const docId = `${department}_${program}_${semester}_${section}`;
            const docRef = doc(db, "timetables", docId);
            const snapshot = await getDoc(docRef);

            if (snapshot.exists()) {
                const data = snapshot.data();
                if (data.slots) {
                    setSlots(data.slots);
                } else {
                    setMessage("No timetable data found for this class.");
                }
            } else {
                setMessage("No timetable found for this selection.");
            }
        } catch (err) {
            console.error("Error fetching timetable:", err);
            setMessage("Failed to fetch timetable. See console for details.");
        } finally {
            setLoading(false);
        }
    };

    const handleCellChange = (slotIndex, day, field, value) => {
        setSlots((prev) => {
            const copy = JSON.parse(JSON.stringify(prev));
            copy[slotIndex].days[day][field] = value;
            return copy;
        });
    };

    const handleSave = async () => {
        setMessage("");
        setLoading(true);

        try {
            const docId = `${department}_${program}_${semester}_${section}`;
            const docRef = doc(db, "timetables", docId);

            const payload = {
                department,
                program,
                semester,
                section,
                slots,
                updatedAt: serverTimestamp(),
            };

            await setDoc(docRef, payload, { merge: true });

            setMessage("Timetable updated successfully.");
            setEditMode(false);
        } catch (err) {
            console.error("Error updating timetable:", err);
            setMessage("Failed to update timetable. See console for details.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>View Timetable</h2>

            {/* Filters */}
            <div className={styles.filters}>
                <label>
                    Department
                    <select
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                        className={styles.select}
                    >
                        {DEPARTMENTS.map((d) => (
                            <option key={d.value} value={d.value}>
                                {d.label}
                            </option>
                        ))}
                    </select>
                </label>

                <label>
                    Program
                    <select
                        value={program}
                        onChange={(e) => setProgram(e.target.value)}
                        className={styles.select}
                    >
                        {PROGRAMS.map((p) => (
                            <option key={p.value} value={p.value}>
                                {p.label}
                            </option>
                        ))}
                    </select>
                </label>

                <label>
                    Semester
                    <select
                        value={semester}
                        onChange={(e) => setSemester(e.target.value)}
                        className={styles.select}
                    >
                        {[...Array(8)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>
                                {i + 1}
                            </option>
                        ))}
                    </select>
                </label>

                <label>
                    Section
                    <select
                        value={section}
                        onChange={(e) => setSection(e.target.value)}
                        className={styles.select}
                    >
                        {["1","2"].map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </label>

                <button
                    type="button"
                    onClick={handleFetch}
                    className={styles.saveBtn}
                    disabled={loading}
                >
                    {loading ? "Loading..." : "Load Timetable"}
                </button>
            </div>

            {/* Timetable Display */}
            {slots.length > 0 && (
                <div className={styles.tableWrapper}>
                    <table className={styles.timetable}>
                        <thead>
                        <tr>
                            <th className={styles.slotHead}>-</th>
                            <th className={styles.slotHead}>Slot</th>
                            {DAYS.map((d) => (
                                <th key={d}>{d}</th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {slots.map((slot, si) => (
                            <React.Fragment key={si}>
                                {/* Subject Row */}
                                <tr className={styles.rowLabel}>
                                    <td className={styles.rowType}>Subject</td>
                                    <td className={styles.slotLabel}>{slot.label}</td>
                                    {DAYS.map((d) => (
                                        <td key={d}>
                                            {editMode ? (
                                                <input
                                                    className={styles.cellInput}
                                                    value={slot.days[d]?.subject || ""}
                                                    onChange={(e) =>
                                                        handleCellChange(si, d, "subject", e.target.value)
                                                    }
                                                />
                                            ) : (
                                                <span className={styles.readOnlyCell}>
                                                    {slot.days[d]?.subject || "-"}
                                                </span>
                                            )}
                                        </td>
                                    ))}
                                </tr>

                                {/* Room Row */}
                                <tr>
                                    <td className={styles.rowType}>Room</td>
                                    <td className={styles.slotLabel}></td>
                                    {DAYS.map((d) => (
                                        <td key={d}>
                                            {editMode ? (
                                                <input
                                                    className={styles.cellInput}
                                                    value={slot.days[d]?.room || ""}
                                                    onChange={(e) =>
                                                        handleCellChange(si, d, "room", e.target.value)
                                                    }
                                                />
                                            ) : (
                                                <span className={styles.readOnlyCell}>
                                                    {slot.days[d]?.room || "-"}
                                                </span>
                                            )}
                                        </td>
                                    ))}
                                </tr>

                                {/* Teacher Row */}
                                <tr>
                                    <td className={styles.rowType}>Teacher</td>
                                    <td className={styles.slotLabel}></td>
                                    {DAYS.map((d) => (
                                        <td key={d}>
                                            {editMode ? (
                                                <input
                                                    className={styles.cellInput}
                                                    value={slot.days[d]?.teacher || ""}
                                                    onChange={(e) =>
                                                        handleCellChange(si, d, "teacher", e.target.value)
                                                    }
                                                />
                                            ) : (
                                                <span className={styles.readOnlyCell}>
                                                    {slot.days[d]?.teacher || "-"}
                                                </span>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            </React.Fragment>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Controls */}
            {slots.length > 0 && !editMode && (
                <div className={styles.controls}>
                    <button
                        className={styles.addSlotBtn}
                        onClick={() => setEditMode(true)}
                    >
                        Update Timetable
                    </button>
                </div>
            )}

            {editMode && (
                <div className={styles.controls}>
                    <button
                        className={styles.saveBtn}
                        onClick={handleSave}
                        disabled={loading}
                    >
                        {loading ? "Saving..." : "Save Changes"}
                    </button>
                    <button
                        className={styles.addSlotBtn}
                        onClick={() => setEditMode(false)}
                    >
                        Cancel
                    </button>
                </div>
            )}

            {message && <p className={styles.message}>{message}</p>}
        </div>
    );
}
